/**
 * Behaviour for sections/evry-article.liquid (ported from the 2024 theme's general.js):
 *  - <evry-reading-progress>: fills a bar at the top of the viewport as the reader scrolls
 *    through the article body (the element whose id is in data-target).
 *  - <evry-share-button>: opens the native share sheet, or copies the link and says so.
 *  - <evry-article-float-nav>: slides the previous/next thumbnails in from the screen edges
 *    once the reader has scrolled past the top of the article.
 *
 * On desktop (≥990px) Horizon scrolls `.page-wrapper` instead of the window, so scroll events
 * and the scroll position come from @theme/scroll-container.
 */
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Calls `callback` at most once per animation frame whenever the page scroll container scrolls
 * or the window resizes. Re-binds when the layout switches between mobile and desktop.
 * @param {() => void} callback
 * @returns {() => void} cleanup
 */
function onScrollFrame(callback) {
  let queued = false;
  /** @type {EventTarget} */
  let scrollTarget = getScrollEventTarget();

  const handler = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      callback();
    });
  };

  const rebind = () => {
    scrollTarget.removeEventListener('scroll', handler);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', handler, { passive: true });
    handler();
  };

  scrollTarget.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', rebind);

  return () => {
    scrollTarget.removeEventListener('scroll', handler);
    window.removeEventListener('resize', handler);
    scrollContainerMediaQuery.removeEventListener('change', rebind);
  };
}

class EvryReadingProgress extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #cleanup;

  connectedCallback() {
    this.#cleanup = onScrollFrame(this.update);
    this.update();
  }

  disconnectedCallback() {
    this.#cleanup?.();
  }

  update = () => {
    const target = this.dataset.target ? document.getElementById(this.dataset.target) : null;
    let progress = 0;

    if (target) {
      // Viewport-relative, so it works whichever element is the scroll container.
      const rect = target.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      progress = scrollable > 0 ? -rect.top / scrollable : rect.top < 0 ? 1 : 0;
    }

    this.style.setProperty('--evry-reading-progress', String(Math.min(1, Math.max(0, progress))));
  };
}

class EvryShareButton extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#share);
  }

  disconnectedCallback() {
    this.querySelector('button')?.removeEventListener('click', this.#share);
    window.clearTimeout(this.#timer);
  }

  #share = async () => {
    const url = this.dataset.url || window.location.href;
    const title = this.dataset.title || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // The reader closed the share sheet: nothing to do.
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#announce();
    } catch {
      window.prompt(title, url);
    }
  };

  #announce() {
    const status = this.querySelector('[role="status"]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = status.dataset.copied ?? '';
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }
}

class EvryArticleFloatNav extends HTMLElement {
  static threshold = 600;

  /** @type {(() => void) | undefined} */
  #cleanup;

  connectedCallback() {
    this.#cleanup = onScrollFrame(this.update);
    this.update();
  }

  disconnectedCallback() {
    this.#cleanup?.();
  }

  update = () => {
    this.toggleAttribute('data-visible', getScrollTop() > EvryArticleFloatNav.threshold);
  };
}

if (!customElements.get('evry-reading-progress')) {
  customElements.define('evry-reading-progress', EvryReadingProgress);
}

if (!customElements.get('evry-share-button')) {
  customElements.define('evry-share-button', EvryShareButton);
}

if (!customElements.get('evry-article-float-nav')) {
  customElements.define('evry-article-float-nav', EvryArticleFloatNav);
}
